import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, Clock, Home, Zap, DollarSign } from "lucide-react";
import {
  getMyLeavesApi,
  adminPendingLeavesApi,
  adminLeaveReportApi,
} from "../../app/api";

export default function LeaveKpiStrip() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = searchParams.get("type");

  const [counts, setCounts] = useState({
    casual: 0,
    halfDay: 0,
    wfh: 0,
    short: 0,
    lwp: 0,
  });
  const [loading, setLoading] = useState(true);

  // Normalize leave type name to standard categories
  const normalizeLeaveType = (typeStr) => {
    if (!typeStr) return "";
    const t = typeStr.toLowerCase();
    if (t.includes("casual")) return "casual";
    if (t.includes("half day")) return "halfDay";
    if (t.includes("work from home") || t.includes("wfh")) return "wfh";
    if (t.includes("short")) return "short";
    if (t.includes("unpaid") || t.includes("lwp") || t.includes("without pay")) return "lwp";
    return "";
  };

  // Map pending leave type_id to standard categories
  const getCategoryFromId = (id) => {
    const numId = Number(id);
    if (numId === 1) return "casual";
    if (numId === 4) return "halfDay";
    if (numId === 5) return "wfh";
    if (numId === 6) return "short";
    if (numId === 9) return "lwp";
    return "";
  };

  const fetchCounts = async () => {
    try {
      setLoading(true);
      let list = [];

      if (location.pathname === "/leaves/pending") {
        const res = await adminPendingLeavesApi();
        if (res.success) {
          list = res.data || [];
        }
      } else if (location.pathname === "/leaves/report") {
        const res = await adminLeaveReportApi();
        if (res.success) {
          list = res.data || [];
        }
      } else {
        // Default to personal leaves (for /leaves/me, /leaves/apply, /leaves/policy, /leaves/holidays)
        const res = await getMyLeavesApi();
        if (res?.success) {
          list = res.data?.leaves || [];
        }
      }

      // Compute counts
      const countsObj = { casual: 0, halfDay: 0, wfh: 0, short: 0, lwp: 0 };
      list.forEach((item) => {
        let cat = "";
        if (item.leave_type_id !== undefined) {
          cat = getCategoryFromId(item.leave_type_id);
        } else if (item.leave_type) {
          cat = normalizeLeaveType(item.leave_type);
        }

        if (cat && countsObj[cat] !== undefined) {
          countsObj[cat] += Number(item.duration || 1); // Summing duration or count of applications. Let's show total days or application counts? The user said "shows on that count... if I click half day shows only leaves that has count". Usually count means number of applications/records. Let's count the number of records!
          // Wait, let's verify if counting records is preferred. Yes, "shows that count got it... shows all casual leaves of all users". So it's count of leave applications. Let's increment by 1.
          // Let's do countsObj[cat] += 1;
        }
      });

      // Let's count records
      const recordCounts = { casual: 0, halfDay: 0, wfh: 0, short: 0, lwp: 0 };
      list.forEach((item) => {
        let cat = "";
        if (item.leave_type_id !== undefined) {
          cat = getCategoryFromId(item.leave_type_id);
        } else if (item.leave_type) {
          cat = normalizeLeaveType(item.leave_type);
        }
        if (cat && recordCounts[cat] !== undefined) {
          recordCounts[cat]++;
        }
      });

      setCounts(recordCounts);
    } catch (err) {
      console.error("Error fetching leave counts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();

    // Listen to custom update events
    window.addEventListener("leave-updated", fetchCounts);
    return () => {
      window.removeEventListener("leave-updated", fetchCounts);
    };
  }, [location.pathname]);

  const scrollToList = () => {
    setTimeout(() => {
      const el = document.getElementById("leave-list-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  const handleCardClick = (kpiLabel) => {
    if (activeFilter === kpiLabel) {
      // Toggle off
      const newParams = Object.fromEntries(searchParams.entries());
      delete newParams.type;
      setSearchParams(newParams);
    } else {
      // Toggle on
      if (
        location.pathname === "/leaves/apply" ||
        location.pathname === "/leaves/policy" ||
        location.pathname === "/leaves/holidays"
      ) {
        navigate(`/leaves/me?type=${encodeURIComponent(kpiLabel)}`);
      } else {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          type: kpiLabel,
        });
      }
    }
    scrollToList();
  };

  const kpis = [
    {
      label: "Casual",
      value: counts.casual,
      subtext: "Personal/General",
      icon: Briefcase,
      bg: "bg-[#E0F2FE]", // Light blue
      iconBg: "bg-blue-200/50",
      iconColor: "text-[#0EA5E9]",
      textColor: "text-blue-900",
      subtextColor: "text-blue-600/80",
    },
    {
      label: "Half Day",
      value: counts.halfDay,
      subtext: "Partial time off",
      icon: Clock,
      bg: "bg-[#FEF3C7]", // Light amber
      iconBg: "bg-amber-200/50",
      iconColor: "text-[#D97706]",
      textColor: "text-amber-900",
      subtextColor: "text-amber-600/80",
    },
    {
      label: "Work From Home",
      value: counts.wfh,
      subtext: "Remote working",
      icon: Home,
      bg: "bg-[#E0FBEA]", // Light green
      iconBg: "bg-green-200/50",
      iconColor: "text-[#22C55E]",
      textColor: "text-green-900",
      subtextColor: "text-green-600/80",
    },
    {
      label: "Short Leave",
      value: counts.short,
      subtext: "Few hours break",
      icon: Zap,
      bg: "bg-[#F3E8FF]", // Light purple
      iconBg: "bg-purple-200/50",
      iconColor: "text-[#9333EA]",
      textColor: "text-purple-900",
      subtextColor: "text-purple-600/80",
    },
    {
      label: "Leave Without Pay [LWP]",
      value: counts.lwp,
      subtext: "Unpaid time off",
      icon: DollarSign,
      bg: "bg-[#FFE4E1]", // Light rose/red
      iconBg: "bg-red-200/50",
      iconColor: "text-[#EF4444]",
      textColor: "text-red-900",
      subtextColor: "text-red-600/80",
    },
  ];

  if (loading) {
    return (
      <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-36 bg-white/10 rounded-2xl border border-white/10" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-7xl mx-auto w-full">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map((kpi, idx) => {
          const isActive = activeFilter === kpi.label;
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: idx * 0.06,
                duration: 0.4,
                type: "spring",
                stiffness: 160,
              }}
              whileHover={{
                y: -6,
                scale: 1.02,
                rotateX: 3,
                rotateY: -3,
              }}
              onClick={() => handleCardClick(kpi.label)}
              className={`
                ${kpi.bg}
                rounded-2xl
                p-5
                relative
                overflow-hidden
                border-2
                ${isActive ? "border-indigo-600 shadow-[0_0_20px_rgba(99,102,241,0.5)] scale-[1.02]" : "border-white/50"}
                shadow-[0_20px_50px_rgba(45,28,106,0.3)]
                hover:shadow-[0_30px_70px_rgba(45,28,106,0.55)]
                group
                cursor-pointer
                flex flex-col justify-between
                h-36
                transition-all duration-300
                active:scale-95
              `}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Animated Light Sweep */}
              <div className="
                absolute -inset-10
                bg-gradient-to-r
                from-transparent
                via-white/20
                to-transparent
                opacity-0
                group-hover:opacity-100
                transition
                duration-700
                rotate-12
                pointer-events-none
              " />

              {/* Header: Icon & Active Indicator */}
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-xl ${kpi.iconBg} flex items-center justify-center shadow-sm ring-1 ring-white/50`}>
                  <kpi.icon className={kpi.iconColor} size={20} />
                </div>
                {isActive ? (
                  <span className="text-[9px] font-black uppercase text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full shadow-sm">
                    Active
                  </span>
                ) : (
                  <span className={`w-2 h-2 rounded-full ${kpi.iconColor} opacity-70 animate-pulse`} />
                )}
              </div>

              {/* Labels and values */}
              <div className="mt-3">
                <p className={`${kpi.textColor} opacity-70 text-[11px] font-bold uppercase tracking-wider`}>
                  {kpi.label}
                </p>
                <h3 className={`text-2xl font-black mt-0.5 tracking-tight ${kpi.textColor}`}>
                  {kpi.value}
                </h3>
                <p className={`text-[10px] mt-0.5 font-semibold ${kpi.subtextColor}`}>
                  {kpi.subtext}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
